import { newBlock, type BlockKind, type EmailBlock } from '@/types/emailBlocks'
import type { Newsletter } from './repository/newsletterRepository'

const KINDS: BlockKind[] = ['text', 'image', 'button', 'divider', 'spacer', 'header', 'heading', 'notice', 'stats', 'card', 'steps', 'footer']

/**
 * A newsletter from a file — 21 Oct 2026.
 *
 * `{ name, subject, preheader, audiences, kind, sender, blocks }` as JSON: the
 * shape a draft is stored in, without its id or history. It opens in the
 * editor unsaved, as a new draft, and is written only when somebody saves it.
 * That is the way back for an email rebuilt outside the CRM, such as one put
 * together again from a draft whose sections were lost. Each section is filled
 * out from `newBlock`, so a file written before a field existed still opens.
 */
export function newsletterFromFile(text: string): Newsletter {
  const v = JSON.parse(text) as Record<string, unknown>
  if (!Array.isArray(v.blocks) || v.blocks.length === 0) throw new Error('The file has no sections (`blocks`).')
  const blocks = (v.blocks as Partial<EmailBlock>[]).map((b, i) => {
    if (!b || !KINDS.includes(b.kind as BlockKind)) throw new Error(`Section ${i + 1} is not a kind the editor knows.`)
    return { ...newBlock(b.kind as BlockKind), ...b } as EmailBlock
  })
  const str = (x: unknown) => (typeof x === 'string' ? x : '')
  const audiences = Array.isArray(v.audiences) ? (v.audiences as string[]) : undefined
  return {
    id: '',
    name: str(v.name),
    subject: str(v.subject),
    preheader: str(v.preheader),
    html: '',
    blocks,
    audiences,
    audience: audiences?.[0] ?? str(v.audience),
    kind: v.kind === 'service' ? 'service' : 'marketing',
    sender: v.sender === 'pro' ? 'pro' : 'consumer',
    status: 'draft',
  }
}
